import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import aeLogo from '@/assets/AE_Logo_Black.webp';
import heroBg from '@/assets/hero-bg.webp';

const BOOKING_URL = 'https://form.fillout.com/t/6Qfrr3aggsus';

const Hero = ({ onBooking }: { onBooking?: () => void }) => {
  const { t } = useTranslation();

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="Luxury interior showroom"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-background/50" />
      </div>

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* H1 and hero image render immediately for LCP */}
        <img src={aeLogo} alt="Atelier Elements" className="h-20 md:h-28 mx-auto mb-8" />
        <span className="text-xs tracking-[0.4em] uppercase text-primary mb-6 block font-medium">
          {t('hero.label')}
        </span>
        <h1 className="text-4xl md:text-6xl lg:text-7xl tracking-tight text-foreground mb-8 font-light uppercase whitespace-nowrap" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {t('hero.title')}
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.3 }}
        >
          <p className="text-lg md:text-xl text-foreground font-light tracking-wide leading-relaxed whitespace-pre-line max-w-2xl mx-auto mb-12">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 w-full max-w-xs sm:max-w-2xl mx-auto">
            <button
              onClick={onBooking}
              className="w-full sm:flex-1 px-8 py-4 text-sm tracking-[0.1em] uppercase bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 rounded-full font-medium whitespace-nowrap text-center"
            >
              {t('hero.cta')}
            </button>
            <button
              onClick={() => document.getElementById('showroom')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:flex-1 px-8 py-4 text-sm tracking-[0.1em] uppercase bg-white border border-foreground/20 text-foreground hover:border-primary hover:text-primary transition-all duration-300 rounded-full font-medium whitespace-nowrap text-center"
            >
              {t('hero.explore')}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator - bouncing white ball */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer"
        onClick={() => document.getElementById('showroom')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
          className="w-14 h-14 rounded-full bg-foreground flex items-center justify-center shadow-lg"
        >
          <ArrowDown className="w-5 h-5 text-background" strokeWidth={2} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
